import React from "react";
import { Box, Grid, TextField, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { fieldStyles } from "services/utils";


const getFieldSx = (fieldName, saveStatus) => ({
  ...fieldStyles,
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset": {
      borderColor:
        saveStatus[fieldName] === "success"
          ? "green"
          : saveStatus[fieldName] === "error"
          ? "red"
          : undefined,
    },
    "& fieldset": {
      borderColor:
        saveStatus[fieldName] === "success"
          ? "green"
          : saveStatus[fieldName] === "error"
          ? "red"
          : undefined,
    },
  },
});

const CompensationEducationForm = ({
  employeeDetails,
  setEmployeeDetails,
  saveStatus,
  handleFieldSave,
}) => {
  const { t } = useTranslation();

  return (
    <Box mt={0}>
      <Typography variant="h5" mb={1} mt={2}>
        {t("admin.employees.employeeDialog.employeeDetails.salaryAndCompensation")}
      </Typography>

      <Grid container spacing={2} mt={1}>
        {/* Basic Salary */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.basicSalary")}
            value={employeeDetails.basicSalary || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, basicSalary: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "basicSalary", {
                basicSalary: e.target.value,
              })
            }
            sx={getFieldSx("basicSalary", saveStatus)}
          />
        </Grid>

        {/* Allowances 1–6 */}
        {[1, 2, 3, 4, 5, 6].map((num) => (
          <Grid item xs={12} md={6} key={`allowance${num}`}>
            <TextField
              fullWidth
              label={t(`admin.employees.employeeDialog.employeeDetails.allowance${num}`)}
              value={employeeDetails[`allowance${num}`] || ""}
              onChange={(e) =>
                setEmployeeDetails((prev) => ({
                  ...prev,
                  [`allowance${num}`]: e.target.value,
                }))
              }
              onBlur={(e) =>
                handleFieldSave(employeeDetails.id, `allowance${num}`, {
                  [`allowance${num}`]: e.target.value,
                })
              }
              sx={getFieldSx(`allowance${num}`, saveStatus)}
            />
          </Grid>
        ))}

        <Grid item xs={12}>
          <Typography variant="h5">
            {t("admin.employees.employeeDialog.employeeDetails.education")}
          </Typography>
        </Grid>

        {/* Education & Certifications */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.university")}
            value={employeeDetails.university || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, university: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "university", {
                university: e.target.value,
              })
            }
            sx={getFieldSx("university", saveStatus)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.graduationYear")}
            value={employeeDetails.graduationYear || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, graduationYear: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "graduationYear", {
                graduationYear: e.target.value,
              })
            }
            sx={getFieldSx("graduationYear", saveStatus)}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.certificates")}
            value={employeeDetails.certificates || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, certificates: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "certificates", {
                certificates: e.target.value,
              })
            }
            sx={getFieldSx("certificates", saveStatus)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.postGraduateUniversity")}
            value={employeeDetails.postGraduateUniversity || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({
                ...prev,
                postGraduateUniversity: e.target.value,
              }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "postGraduateUniversity", {
                postGraduateUniversity: e.target.value,
              })
            }
            sx={getFieldSx("postGraduateUniversity", saveStatus)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.postGraduateCertificate")}
            value={employeeDetails.postGraduateCertificate || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({
                ...prev,
                postGraduateCertificate: e.target.value,
              }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "postGraduateCertificate", {
                postGraduateCertificate: e.target.value,
              })
            }
            sx={getFieldSx("postGraduateCertificate", saveStatus)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.postGraduateGraduationYears")}
            value={employeeDetails.postGraduateGraduationYears || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({
                ...prev,
                postGraduateGraduationYears: e.target.value,
              }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "postGraduateGraduationYears", {
                postGraduateGraduationYears: e.target.value,
              })
            }
            sx={getFieldSx("postGraduateGraduationYears", saveStatus)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.cep")}
            value={employeeDetails.cep || ""}
            onChange={(e) => setEmployeeDetails((prev) => ({ ...prev, cep: e.target.value }))}
            onBlur={(e) => handleFieldSave(employeeDetails.id, "cep", { cep: e.target.value })}
            sx={getFieldSx("cep", saveStatus)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label={t("admin.employees.employeeDialog.employeeDetails.yearsOfRelevantExperience")}
            value={employeeDetails.yearsOfRelevantExperience || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({
                ...prev,
                yearsOfRelevantExperience: e.target.value,
              }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "yearsOfRelevantExperience", {
                yearsOfRelevantExperience: e.target.value,
              })
            }
            sx={getFieldSx("yearsOfRelevantExperience", saveStatus)}
            inputProps={{ min: 0 }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h5">
            {t("admin.employees.employeeDialog.employeeDetails.licenses")}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.professionalLicense")}
            value={employeeDetails.professionalLicense || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({
                ...prev,
                professionalLicense: e.target.value,
              }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "professionalLicense", {
                professionalLicense: e.target.value,
              })
            }
            sx={getFieldSx("professionalLicense", saveStatus)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.professionalLicenseNumber")}
            value={employeeDetails.professionalLicenseNumber || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({
                ...prev,
                professionalLicenseNumber: e.target.value,
              }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "professionalLicenseNumber", {
                professionalLicenseNumber: e.target.value,
              })
            }
            sx={getFieldSx("professionalLicenseNumber", saveStatus)}
          />
        </Grid>

      </Grid>
    </Box>
  );
};

CompensationEducationForm.propTypes = {
  employeeDetails: PropTypes.object.isRequired,
  setEmployeeDetails: PropTypes.func.isRequired,
  saveStatus: PropTypes.object.isRequired,
  handleFieldSave: PropTypes.func.isRequired,
};

export default CompensationEducationForm;
